import { useQuery } from '@tanstack/react-query'
import { Film, RotateCcw, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { Button, IconButton } from '@/components/ui/Button'
import { formatBytes } from '@/utils/format'
import { videoQuery } from '../api'
import type { VideoRef } from '../types'
import { uploadStreamingVideo } from '../uploads'
import { VideoBlock } from './VideoPlayer'

/**
 * Picks a video, sends it to the server in chunks, then follows the conversion. The parent keeps the
 * video id to save with the material (it can be saved while the conversion is still running).
 */
export function VideoUploadField({
  initial,
  onChange,
  onBusyChange,
  error,
}: {
  initial: VideoRef | null
  onChange: (videoId: number | null) => void
  onBusyChange: (busy: boolean) => void
  error?: string
}) {
  const { t } = useTranslation('lms')
  const input = useRef<HTMLInputElement>(null)
  const abort = useRef<AbortController | null>(null)
  const [videoId, setVideoId] = useState<number | null>(initial?.id ?? null)
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const state = useQuery({ ...videoQuery(videoId ?? 0), enabled: videoId !== null && progress === null })
  const video = state.data ?? (initial?.id === videoId ? initial : null)

  useEffect(() => () => abort.current?.abort(), [])

  const start = async (chosen: File) => {
    setFile(chosen)
    setFailure(null)
    setProgress(0)
    onBusyChange(true)
    abort.current = new AbortController()
    try {
      const uploaded = await uploadStreamingVideo(chosen, setProgress, abort.current.signal)
      setVideoId(uploaded.id)
      onChange(uploaded.id)
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        const apiError = toApiError(e)
        setFailure(apiError.violations[0]?.message ?? apiError.message)
      }
    } finally {
      setProgress(null)
      onBusyChange(false)
    }
  }

  const clear = () => {
    abort.current?.abort()
    setVideoId(null)
    setFile(null)
    onChange(null)
  }

  return (
    <div className="space-y-3">
      <input
        ref={input}
        type="file"
        hidden
        accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void start(f)
          e.target.value = ''
        }}
      />
      {progress !== null ? (
        <div className="rounded-control border border-line p-3">
          <div className="flex items-center gap-2 text-sm">
            <Film className="size-4 shrink-0 text-brand-600" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate font-medium text-ink">{file?.name}</span>
            <span className="text-xs text-muted tabular">{Math.round(progress * 100)} %</span>
            <IconButton size="icon-sm" icon={<X />} label={t('Cancel the upload')} onClick={clear} />
          </div>
          <div
            className="mt-2 h-1.5 rounded-full bg-surface-sunken"
            role="progressbar"
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('Upload progress')}
          >
            <div
              className="h-full rounded-full bg-brand-500 transition-all"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-muted">
            {t('Uploading {{size}} — keep this window open.', { size: file ? formatBytes(file.size) : '' })}
          </p>
        </div>
      ) : video ? (
        <div className="space-y-2">
          <VideoBlock video={video} title={video.name} />
          <div className="flex items-center justify-between gap-2 text-xs text-muted">
            <span className="truncate">{video.name}</span>
            <Button size="sm" variant="ghost" leftIcon={<RotateCcw />} onClick={() => input.current?.click()}>
              {t('Replace the video')}
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="secondary" leftIcon={<Film />} onClick={() => input.current?.click()}>
          {t('Choose a video')}
        </Button>
      )}
      <p className="text-xs text-muted">
        {t(
          'MP4, MOV, WebM or MKV, up to 2 GB. It is converted for smooth streaming on any connection; this can take a few minutes.',
        )}
      </p>
      {failure || error ? <p className="text-xs text-danger-700">{failure ?? error}</p> : null}
    </div>
  )
}
